/**
 * 쿠키 유틸리티 함수
 */

export interface CookieOptions {
  expires?: number; // days
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * 쿠키를 설정합니다
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    expires = 7, // 기본 7일
    path = '/',
    domain,
    secure = window.location.protocol === 'https:',
    sameSite = 'Lax',
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * 쿠키를 읽어옵니다
 */
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    // name= 또는 name= (인코딩된 이름) 찾기
    if (cookie.startsWith(name + '=')) {
      const value = cookie.substring(name.length + 1);
      try {
        return decodeURIComponent(value);
      } catch {
        return value; // 디코딩 실패시 원본 반환
      }
    }
  }
  return null;
};

/**
 * 쿠키를 삭제합니다
 */
export const deleteCookie = (name: string, path: string = '/'): void => {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
};

/**
 * 인증 토큰 전용 쿠키 키
 */
export const AUTH_TOKEN_COOKIE_KEY = 'front_auth_token';

/**
 * 리프레시 토큰 저장 키 (localStorage)
 */
export const REFRESH_TOKEN_STORAGE_KEY = 'front_refresh_token';

/**
 * 디바이스 ID 저장 키 (localStorage)
 */
export const DEVICE_ID_STORAGE_KEY = 'front_device_id';

/**
 * 인증 토큰을 쿠키에 저장합니다
 */
export const setAuthTokenCookie = (token: string, expiresInHours: number = 2): void => {
  setCookie(AUTH_TOKEN_COOKIE_KEY, token, {
    expires: expiresInHours / 24, // hours to days
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax',
  });
};

/**
 * 쿠키에서 인증 토큰을 읽어옵니다
 */
export const getAuthTokenCookie = (): string | null => {
  return getCookie(AUTH_TOKEN_COOKIE_KEY);
};

/**
 * 쿠키에서 인증 토큰을 삭제합니다
 */
export const deleteAuthTokenCookie = (): void => {
  deleteCookie(AUTH_TOKEN_COOKIE_KEY);
};

/**
 * 리프레시 토큰을 localStorage에 저장합니다
 */
export const setRefreshTokenStorage = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage 사용 불가 시 무시
  }
};

/**
 * localStorage에서 리프레시 토큰을 읽어옵니다
 */
export const getRefreshTokenStorage = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * localStorage에서 리프레시 토큰을 삭제합니다
 */
export const deleteRefreshTokenStorage = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // 무시
  }
};

/**
 * device_id를 가져옵니다. 없으면 UUID 생성 후 저장합니다
 */
export const getOrCreateDeviceId = (): string => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID?.() ?? `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    }
    return deviceId;
  } catch {
    return 'string';
  }
};

/**
 * 동기화 방 관련 쿠키 키
 */
export const SYNC_ROOM_ID_COOKIE_KEY = 'rd';
export const SYNC_JOIN_CODE_COOKIE_KEY = 'c';

/**
 * 동기화 방 ID와 Join Code를 쿠키에 저장합니다
 * 
 * 주의: httpOnly 쿠키는 프론트엔드에서 직접 설정할 수 없습니다.
 * 서버에서 Set-Cookie 헤더로 설정해야 합니다.
 * 현재는 일반 쿠키로 저장하며, 나중에 서버에서 httpOnly로 변경할 수 있습니다.
 */
export const setSyncRoomCookies = (roomId: string, joinCode: string, expiresInHours: number = 2): void => {
  setCookie(SYNC_ROOM_ID_COOKIE_KEY, roomId, {
    expires: expiresInHours / 24, // hours to days
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax',
  });
  
  setCookie(SYNC_JOIN_CODE_COOKIE_KEY, joinCode, {
    expires: expiresInHours / 24, // hours to days
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax',
  });
};

/**
 * 쿠키에서 동기화 방 ID를 읽어옵니다
 */
export const getSyncRoomIdCookie = (): string | null => {
  return getCookie(SYNC_ROOM_ID_COOKIE_KEY);
};

/**
 * 쿠키에서 동기화 Join Code를 읽어옵니다
 */
export const getSyncJoinCodeCookie = (): string | null => {
  return getCookie(SYNC_JOIN_CODE_COOKIE_KEY);
};

/**
 * 동기화 방 관련 쿠키를 삭제합니다
 */
export const deleteSyncRoomCookies = (): void => {
  deleteCookie(SYNC_ROOM_ID_COOKIE_KEY);
  deleteCookie(SYNC_JOIN_CODE_COOKIE_KEY);
};


